import { API_PATHS } from '@/constants/api-paths'
import { api } from '@/lib/api'
import type { ApiResponse } from '@/types'

export type HelpSupportStatus = 'open' | 'in_progress' | 'resolved' | 'closed'

export interface HelpSupportOrg {
  id: number
  instance_id: string
  name: string
  identifier: string
}

export interface HelpSupportAttachment {
  id: number
  name: string
  size: number
  content_type: string
  /** Signed or public link; null when storage can't build one. */
  url: string | null
}

export interface HelpSupportReply {
  id: number
  body: string
  /** Master admin note for the team only; customers never receive these. */
  is_internal: boolean
  created_at: string | null
  organization: HelpSupportOrg | null
  author_name: string
  attachments: HelpSupportAttachment[]
}

export interface HelpSupportAssignee {
  id: number
  name: string
}

/** Images and PDFs, at most 3 files of 5 MB each (checked again by the server). */
export const HELP_ATTACHMENT_ACCEPT = '.png,.jpg,.jpeg,.webp,.pdf'
export const HELP_ATTACHMENT_MAX_FILES = 3
export const HELP_ATTACHMENT_MAX_BYTES = 5 * 1024 * 1024

/** Why these files can't be sent, or null. */
export function helpAttachmentError(files: File[]): string | null {
  if (files.length > HELP_ATTACHMENT_MAX_FILES) return `Attach up to ${HELP_ATTACHMENT_MAX_FILES} files.`
  for (const file of files) {
    if (!/\.(png|jpe?g|webp|pdf)$/i.test(file.name)) return `${file.name}: only images (PNG, JPG, WEBP) and PDFs can be attached.`
    if (file.size > HELP_ATTACHMENT_MAX_BYTES) return `${file.name} is larger than 5 MB.`
  }
  return null
}

/** JSON when there are no files, multipart with an "attachments" field per file otherwise. */
export function helpMessageBody(fields: Record<string, string>, files: File[] = []) {
  if (files.length === 0) return { data: fields, config: undefined }
  const formData = new FormData()
  Object.entries(fields).forEach(([key, value]) => formData.append(key, value))
  files.forEach((file) => formData.append('attachments', file, file.name))
  return { data: formData, config: { headers: { 'Content-Type': 'multipart/form-data' } } }
}

export interface HelpSupportRequest {
  id: number
  reference: string
  subject: string
  message: string
  status: HelpSupportStatus
  replies: HelpSupportReply[]
  attachments: HelpSupportAttachment[]
  created_at: string | null
  updated_at: string | null
  parent_organization: HelpSupportOrg | null
  organization?: HelpSupportOrg | null
  /** Master admin reply/resolve only: the customer address that was emailed, or null when no email went out. */
  emailed_to?: string | null
  /** Master admin only: who raised the request; reply emails go to them. */
  raised_by?: { name: string; email: string | null } | null
  /** Master admin only: the team member handling it. */
  assigned_to?: HelpSupportAssignee | null
}

export interface HelpSupportList {
  organization: HelpSupportOrg | null
  parent_organization: HelpSupportOrg | null
  can_receive: boolean
  own: HelpSupportRequest[]
  incoming: HelpSupportRequest[]
}

function unwrap<T>(payload: ApiResponse<T>): T {
  return payload.data
}

export const helpSupportApi = {
  list: async (): Promise<HelpSupportList> => {
    const response = await api.get<ApiResponse<HelpSupportList>>(API_PATHS.helpSupport.list)
    return unwrap(response.data)
  },
  add: async (payload: { subject: string; message: string }, files: File[] = []): Promise<HelpSupportRequest> => {
    const { data, config } = helpMessageBody(payload, files)
    const response = await api.post<ApiResponse<HelpSupportRequest>>(API_PATHS.helpSupport.add, data, config)
    return unwrap(response.data)
  },
  update: async (
    id: number,
    payload: { status: HelpSupportStatus },
  ): Promise<HelpSupportRequest> => {
    const response = await api.post<ApiResponse<HelpSupportRequest>>(
      API_PATHS.helpSupport.update(id),
      payload,
    )
    return unwrap(response.data)
  },
  reply: async (id: number, body: string, files: File[] = []): Promise<HelpSupportRequest> => {
    const { data, config } = helpMessageBody({ body }, files)
    const response = await api.post<ApiResponse<HelpSupportRequest>>(
      API_PATHS.helpSupport.reply(id),
      data,
      config,
    )
    return unwrap(response.data)
  },
}
